import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize, forkJoin } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type ControlPlaneAuthType = 'None' | 'HeaderSecret' | 'BearerToken' | 'CustomHeaders';
type TenantIntegrationInstallationStatus = 'Pending' | 'InstallFailed' | 'Active' | 'Suspended' | 'Disabled' | 'Deleted';
type SupportedEventPattern = string;

const SUPPORTED_EVENT_DOMAIN_TO_PATTERN: Record<string, SupportedEventPattern> = {
  tenant: 'tenant.*',
  user: 'user.*',
  servicenumber: 'service_number.*',
  contact: 'contact.*',
  employee: 'employee.*',
  visitor: 'visitor.*',
  group: 'group.*',
  addressbook: 'addressbook.*',
  notice: 'notice.*',
  session: 'session.*'
};

const RECOMMENDED_SUPPORTED_EVENTS: SupportedEventPattern[] = [
  'tenant.*',
  'service_number.*',
  'contact.*',
  'employee.*',
  'user.*',
  'visitor.*',
  'group.*',
  'addressbook.*',
  'notice.*',
  'session.*'
];

const SUPPORTED_EVENT_PATTERN_LABEL_KEYS: Record<SupportedEventPattern, string> = {
  'tenant.*': 'official.third-party-app.supported-event.tenant',
  'service_number.*': 'official.third-party-app.supported-event.service-number',
  'contact.*': 'official.third-party-app.supported-event.contact',
  'employee.*': 'official.third-party-app.supported-event.employee',
  'user.*': 'official.third-party-app.supported-event.user',
  'visitor.*': 'official.third-party-app.supported-event.visitor',
  'group.*': 'official.third-party-app.supported-event.group',
  'addressbook.*': 'official.third-party-app.supported-event.addressbook',
  'notice.*': 'official.third-party-app.supported-event.notice',
  'session.*': 'official.third-party-app.supported-event.session'
};

const EVENT_TYPE_DESCRIPTION_KEYS: Record<string, string> = {
  'contact.created': 'tenant.detail.integration-apps.event-log.description.contact-created',
  'contact.service_number_followed': 'tenant.detail.integration-apps.event-log.description.contact-service-number-followed'
};

interface IntegrationAppDefinition {
  id: string;
  appId: string;
  appName: string;
  provider: string;
  supportedEvents: string[];
  controlPlaneBaseUrl: string;
  displayControlPlaneBaseUrl: string;
  displayInstallUrl: string;
  controlPlaneAuthType: ControlPlaneAuthType;
  controlPlaneSecret?: string;
  controlPlaneHeaders: Record<string, string>;
  controlPlaneEndpoints: Record<string, string>;
  status: 'Active' | 'Disabled' | 'Deleted';
}

interface TenantIntegrationInstallation {
  id: string;
  instanceId?: string;
  integrationId: string;
  appId: string;
  tenantId: string;
  tenantType?: string;
  externalTenantId?: string;
  instanceSecret?: string;
  appSecret?: string;
  webhookUrl?: string;
  subscribedEvents: string[];
  installAckMode?: 'Sync' | 'Async';
  installRequestedAt?: number;
  installCompletedAt?: number;
  lastCallbackAt?: number;
  operatorId?: string;
  status?: TenantIntegrationInstallationStatus;
  createTime?: number;
  updateTime?: number;
}

interface TenantIntegrationAppViewModel extends IntegrationAppDefinition {
  installed: boolean;
  installUrl: string | null;
  installation: TenantIntegrationInstallation | null;
}

interface IntegrationEventLogItem {
  eventId: string;
  eventType: string;
  appId: string;
  instanceId?: string;
  integrationId: string;
  tenantId: string;
  externalTenantId?: string;
  occurredAt?: number;
  published?: boolean;
}

interface IntegrationEventLogPage {
  items?: IntegrationEventLogItem[];
  totalCount?: number;
  pageIndex?: number;
  hasNextPage?: boolean;
}

@Component({
  selector: 'tenant-integration-apps',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzCardModule,
    NzCheckboxModule,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    NzModalModule,
    NzSelectModule,
    NzSpinModule,
    NzTagModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-integration-apps.component.html',
  styleUrls: ['./tenant-integration-apps.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantIntegrationAppsComponent implements OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly installedStatuses = new Set<TenantIntegrationInstallationStatus>(['Pending', 'Active', 'Suspended', 'Disabled']);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);

  @Input() tenantId = '';
  @Input() tenantName = '';
  @Input() tenantType = '';

  loading = false;
  operatingAppId: string | null = null;
  readonly visibleSecretInstanceIds = new Set<string>();
  installations: TenantIntegrationInstallation[] = [];
  apps: TenantIntegrationAppViewModel[] = [];
  updateModalVisible = false;
  updateTargetApp: TenantIntegrationAppViewModel | null = null;
  eventLogModalVisible = false;
  eventLogTargetApp: TenantIntegrationAppViewModel | null = null;
  eventLogLoading = false;
  eventLogs: IntegrationEventLogItem[] = [];
  eventLogTotalCount = 0;
  eventLogPageIndex = 0;
  eventLogHasNextPage = false;
  readonly eventLogPageSize = 10;
  readonly updateForm = this.fb.nonNullable.group({
    webhookUrl: ['', [Validators.required]],
    subscribedEvents: this.fb.nonNullable.control<string[]>([], Validators.required)
  });

  get supportedEventOptions(): string[] {
    return this.normalizeSupportedEvents([...RECOMMENDED_SUPPORTED_EVENTS, ...(this.updateTargetApp?.supportedEvents ?? [])]);
  }

  get selectedSubscribedEvents(): string[] {
    return this.normalizeSupportedEvents(this.updateForm.controls.subscribedEvents.value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenantId'] && changes['tenantId'].currentValue !== changes['tenantId'].previousValue) {
      this.loadApps(changes['tenantId'].currentValue);
    }
  }

  get hasApps(): boolean {
    return this.apps.length > 0;
  }

  get installedApps(): TenantIntegrationAppViewModel[] {
    return this.apps.filter(item => item.installed);
  }

  get uninstalledApps(): TenantIntegrationAppViewModel[] {
    return this.apps.filter(item => !item.installed);
  }

  get emptyDescription(): string {
    const tenantName = this.tenantName?.trim() || this.i18n.fanyi('tenant.detail.integration-apps.default-tenant-name');
    return this.i18n.fanyi('tenant.detail.integration-apps.empty.description', { tenantName });
  }

  getInstallationStatusText(status?: TenantIntegrationInstallationStatus): string {
    if (!status) {
      return '-';
    }

    return this.i18n.fanyi(`tenant.detail.integration-apps.installation-status.${status}` as any);
  }

  getInstalledStatusTagClass(status?: TenantIntegrationInstallationStatus): string {
    if (status === 'Suspended' || status === 'Disabled') {
      return 'status-tag-suspended';
    }

    if (status === 'Pending') {
      return 'status-tag-pending';
    }

    if (status === 'InstallFailed' || status === 'Deleted') {
      return 'status-tag-danger';
    }

    return 'status-tag-installed';
  }

  getSupportedEventLabel(pattern: SupportedEventPattern): string {
    const labelKey = SUPPORTED_EVENT_PATTERN_LABEL_KEYS[pattern];
    return labelKey ? this.i18n.fanyi(labelKey) : pattern;
  }

  isOperating(app: TenantIntegrationAppViewModel): boolean {
    return this.operatingAppId === this.getOperationKey(app);
  }

  refreshApps(): void {
    if (this.loading || this.operatingAppId) {
      return;
    }

    this.loadApps(this.tenantId);
  }

  openInstall(app: TenantIntegrationAppViewModel): void {
    const appId = String(app.appId ?? '').trim();
    const tenantId = String(this.tenantId ?? '').trim();
    const tenantType = String(this.tenantType ?? '').trim();
    const operatorId = this.getOperatorId();
    if (!appId || !tenantId || !tenantType || !operatorId) {
      this.message.error(this.i18n.fanyi('common.load-failed'));
      return;
    }

    if (this.operatingAppId) {
      return;
    }

    this.operatingAppId = this.getOperationKey(app);
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.OPENAPI_INTEGRATION_APP_INSTANCE_SYSTEM_V1_INSTALL, {
        appId,
        tenantId,
        tenantType,
        operatorId
      })
      .pipe(
        finalize(() => {
          this.operatingAppId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        if (!result.success) {
          this.message.error(result.msg || this.i18n.fanyi('common.load-failed'));
          return;
        }

        this.message.success(result.msg || this.i18n.fanyi('common.save-success'));
        this.refreshTenantInstallations();
      });
  }

  suspendInstall(app: TenantIntegrationAppViewModel): void {
    const instanceId = this.getInstanceId(app);
    this.changeInstallationState(ConstantUtil.OPENAPI_INTEGRATION_APP_INSTANCE_SYSTEM_V1_SUSPEND, { instanceId }, app);
  }

  resumeInstall(app: TenantIntegrationAppViewModel): void {
    const instanceId = this.getInstanceId(app);
    this.changeInstallationState(ConstantUtil.OPENAPI_INTEGRATION_APP_INSTANCE_SYSTEM_V1_RESUME, { instanceId }, app);
  }

  uninstallInstall(app: TenantIntegrationAppViewModel): void {
    const instanceId = this.getInstanceId(app);
    this.changeInstallationState(ConstantUtil.OPENAPI_INTEGRATION_APP_INSTANCE_SYSTEM_V1_UNINSTALL, { instanceId }, app);
  }

  rotateInstallSecret(app: TenantIntegrationAppViewModel): void {
    const instanceId = this.getInstanceId(app);
    if (!instanceId) {
      this.message.error(this.i18n.fanyi('common.load-failed'));
      return;
    }

    this.changeInstallationState(ConstantUtil.OPENAPI_INTEGRATION_APP_INSTANCE_SYSTEM_V1_ROTATE_SECRET, { instanceId }, app);
  }

  openUpdateModal(app: TenantIntegrationAppViewModel): void {
    this.updateTargetApp = app;
    this.updateForm.reset({
      webhookUrl: String(app.installation?.webhookUrl ?? '').trim(),
      subscribedEvents: this.normalizeSupportedEvents(app.installation?.subscribedEvents ?? [])
    });
    this.updateModalVisible = true;
  }

  /**
   * 開啟目前安裝實例的 Webhook 事件紀錄，只使用 Admin 唯讀查詢接口。
   */
  openEventLogModal(app: TenantIntegrationAppViewModel): void {
    if (!this.getInstanceId(app) || !String(this.tenantId ?? '').trim()) {
      this.message.error(this.i18n.fanyi('common.load-failed'));
      return;
    }

    this.eventLogTargetApp = app;
    this.eventLogModalVisible = true;
    this.loadEventLogs(0);
  }

  /**
   * 關閉事件紀錄視窗並清除目前查詢狀態。
   */
  closeEventLogModal(): void {
    this.eventLogModalVisible = false;
    this.eventLogTargetApp = null;
    this.eventLogs = [];
    this.eventLogTotalCount = 0;
    this.eventLogPageIndex = 0;
    this.eventLogHasNextPage = false;
  }

  /**
   * 載入指定頁面的事件紀錄。
   */
  loadEventLogs(pageIndex: number): void {
    const app = this.eventLogTargetApp;
    const instanceId = app ? this.getInstanceId(app) : '';
    const tenantId = String(this.tenantId ?? '').trim();
    if (!app || !instanceId || !tenantId || this.eventLogLoading) {
      return;
    }

    this.eventLogLoading = true;
    this.cdr.markForCheck();
    this.http
      .post(ConstantUtil.ADMIN_INTEGRATION_EVENT_LOG_V1_LIST, {
        appId: app.appId,
        instanceId,
        integrationId: instanceId,
        tenantId,
        pageIndex,
        pageSize: this.eventLogPageSize
      })
      .pipe(
        finalize(() => {
          this.eventLogLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        if (!result.success) {
          this.message.error(result.msg || this.i18n.fanyi('common.load-failed'));
          return;
        }

        const data = (result.data ?? {}) as IntegrationEventLogPage;
        this.eventLogs = Array.isArray(data.items) ? data.items : [];
        this.eventLogTotalCount = Number(data.totalCount ?? this.eventLogs.length);
        this.eventLogPageIndex = Number(data.pageIndex ?? pageIndex);
        this.eventLogHasNextPage = Boolean(data.hasNextPage);
      });
  }

  /**
   * 格式化事件發生時間，無效或缺失值以預設符號呈現。
   */
  formatEventOccurredAt(occurredAt?: number): string {
    if (!occurredAt) {
      return '-';
    }

    const date = new Date(occurredAt);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
  }

  /**
   * 取得事件投遞結果文案。
   */
  getEventPublishStatusText(published?: boolean): string {
    return this.i18n.fanyi(
      published ? 'tenant.detail.integration-apps.event-log.status.published' : 'tenant.detail.integration-apps.event-log.status.failed'
    );
  }

  getEventTypeDescription(eventType: string): string {
    const descriptionKey = EVENT_TYPE_DESCRIPTION_KEYS[eventType.trim().toLowerCase()];
    return descriptionKey ? this.i18n.fanyi(descriptionKey) : '';
  }

  closeUpdateModal(): void {
    this.updateModalVisible = false;
    this.updateTargetApp = null;
  }

  submitUpdate(): void {
    if (this.updateForm.invalid || !this.updateTargetApp) {
      Object.values(this.updateForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      return;
    }

    const instanceId = this.getInstanceId(this.updateTargetApp);
    if (!instanceId) {
      this.message.error(this.i18n.fanyi('common.load-failed'));
      return;
    }

    const value = this.updateForm.getRawValue();
    this.changeInstallationState(
      ConstantUtil.OPENAPI_INTEGRATION_APP_INSTANCE_SYSTEM_V1_UPDATE,
      {
        instanceId,
        integrationId: instanceId,
        webhookUrl: value.webhookUrl.trim(),
        subscribedEvents: this.normalizeSupportedEvents(value.subscribedEvents)
      },
      this.updateTargetApp,
      () => this.closeUpdateModal()
    );
  }

  isRecommendedSubscribedEventSelected(pattern: SupportedEventPattern): boolean {
    return this.selectedSubscribedEvents.includes(pattern);
  }

  toggleRecommendedSubscribedEvent(pattern: SupportedEventPattern, checked: boolean): void {
    const current = this.selectedSubscribedEvents;
    const next = checked ? [...current, pattern] : current.filter(item => item !== pattern);
    this.setSubscribedEvents(next);
  }

  removeSubscribedEvent(pattern: SupportedEventPattern): void {
    this.setSubscribedEvents(this.selectedSubscribedEvents.filter(item => item !== pattern));
  }

  private loadApps(tenantIdInput?: string): void {
    const tenantId = String(tenantIdInput ?? this.tenantId).trim();
    if (!tenantId) {
      this.installations = [];
      this.apps = [];
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    forkJoin({
      systemApps: this.http.post(ConstantUtil.OPENAPI_INTEGRATION_APP_V1_LIST, { status: 'Active' }),
      tenantInstallations: this.http.get(
        ConstantUtil.OPENAPI_INTEGRATION_APP_INSTANCE_SYSTEM_V1_LIST,
        { tenantId },
        { aileMode: 'develop' }
      )
    })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(({ systemApps, tenantInstallations }) => {
        if (!systemApps.success) {
          this.installations = [];
          this.apps = [];
          return;
        }

        this.installations = tenantInstallations.success
          ? Array.isArray((tenantInstallations.data as { items?: unknown[] } | undefined)?.items)
            ? ((tenantInstallations.data as { items: TenantIntegrationInstallation[] }).items ?? [])
            : Array.isArray(tenantInstallations.data)
              ? (tenantInstallations.data as TenantIntegrationInstallation[])
              : []
          : [];
        const systemDefinitions = (
          Array.isArray((systemApps.data as { items?: unknown[] } | undefined)?.items)
            ? ((systemApps.data as { items: any[] }).items ?? [])
            : Array.isArray(systemApps.data)
              ? (systemApps.data as any[])
              : []
        )
          .map(item => this.toIntegrationAppDefinition(item))
          .filter(item => item.status !== 'Deleted');
        this.apps = this.mergeApps(systemDefinitions, this.installations);
      });
  }

  private changeInstallationState(
    url: string,
    payload: Record<string, unknown>,
    app: TenantIntegrationAppViewModel,
    onSuccess?: () => void
  ): void {
    if (this.operatingAppId) {
      return;
    }

    this.operatingAppId = this.getOperationKey(app);
    this.cdr.markForCheck();
    this.http
      .post(url, payload)
      .pipe(
        finalize(() => {
          this.operatingAppId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        if (!result.success) {
          this.message.error(result.msg || this.i18n.fanyi('common.load-failed'));
          return;
        }

        onSuccess?.();
        this.message.success(result.msg || this.i18n.fanyi('common.save-success'));
        this.refreshTenantInstallations();
      });
  }

  private getOperationKey(app: TenantIntegrationAppViewModel): string {
    return String(app.appId || app.id)
      .trim()
      .toUpperCase();
  }

  private refreshTenantInstallations(): void {
    const tenantId = String(this.tenantId ?? '').trim();
    if (!tenantId) {
      return;
    }

    this.http.get(ConstantUtil.OPENAPI_INTEGRATION_APP_INSTANCE_SYSTEM_V1_LIST, { tenantId }, { aileMode: 'develop' }).subscribe(result => {
      if (!result.success) {
        this.message.error(result.msg || this.i18n.fanyi('common.load-failed'));
        return;
      }

      this.installations = Array.isArray((result.data as { items?: unknown[] } | undefined)?.items)
        ? ((result.data as { items: TenantIntegrationInstallation[] }).items ?? [])
        : Array.isArray(result.data)
          ? (result.data as TenantIntegrationInstallation[])
          : [];
      this.apps = this.mergeApps(
        this.apps.map(item => this.toIntegrationAppDefinition(item)),
        this.installations
      );
      this.cdr.markForCheck();
    });
  }

  private getOperatorId(): string {
    try {
      const loginInfo = JSON.parse(localStorage.getItem('loginInfo') || '{}');
      return String(loginInfo.accountId ?? loginInfo.operatorId ?? loginInfo.userId ?? loginInfo.id ?? '').trim();
    } catch {
      return '';
    }
  }

  /**
   * 取得租戶安裝實例 ID。
   */
  getInstanceId(app: TenantIntegrationAppViewModel): string {
    return String(app.installation?.instanceId ?? app.installation?.integrationId ?? app.installation?.id ?? '').trim();
  }

  /**
   * 取得租戶安裝實例密鑰。
   */
  getInstanceSecret(app: TenantIntegrationAppViewModel): string {
    return String(app.installation?.instanceSecret ?? app.installation?.appSecret ?? '').trim();
  }

  /**
   * 檢查指定實例密鑰目前是否為明文可見狀態。
   */
  isInstanceSecretVisible(instanceId: string): boolean {
    return this.visibleSecretInstanceIds.has(instanceId);
  }

  /**
   * 切換實例密鑰之明文/遮蔽可見性狀態。
   */
  toggleInstanceSecretVisibility(instanceId: string): void {
    if (!instanceId) {
      return;
    }
    if (this.visibleSecretInstanceIds.has(instanceId)) {
      this.visibleSecretInstanceIds.delete(instanceId);
    } else {
      this.visibleSecretInstanceIds.add(instanceId);
    }
    this.cdr.markForCheck();
  }

  /**
   * 取得展示用的實例密鑰內容（預設經脫敏密碼管理處理不可全見）。
   */
  getDisplayInstanceSecret(app: TenantIntegrationAppViewModel): string {
    const secret = this.getInstanceSecret(app);
    if (!secret) {
      return '-';
    }
    const instanceId = this.getInstanceId(app);
    if (instanceId && this.isInstanceSecretVisible(instanceId)) {
      return secret;
    }
    return this.maskSecret(secret);
  }

  /**
   * 對密鑰字串進行脫敏處理。
   */
  private maskSecret(secret: string): string {
    if (!secret) {
      return '';
    }
    if (secret.length <= 8) {
      return '*'.repeat(Math.max(8, secret.length));
    }
    return `${secret.slice(0, 4)}${'*'.repeat(Math.max(4, secret.length - 8))}${secret.slice(-4)}`;
  }

  /**
   * 複製指定文字到剪貼簿。
   */
  copyText(text: string): void {
    const content = text?.trim() ?? '';
    if (!content) {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
      return;
    }

    const nav = navigator as Navigator & {
      clipboard?: {
        writeText(val: string): Promise<void>;
      };
    };

    if (nav.clipboard?.writeText) {
      nav.clipboard
        .writeText(content)
        .then(() => this.message.success(this.i18n.fanyi('common.copy-success')))
        .catch(() => this.copyTextWithFallback(content));
      return;
    }

    this.copyTextWithFallback(content);
  }

  /**
   * 當瀏覽器未提供或不允許呼叫剪貼簿 API 時之向下相容降級複製方案。
   */
  private copyTextWithFallback(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      const ok = document.execCommand('copy');
      if (ok) {
        this.message.success(this.i18n.fanyi('common.copy-success'));
      } else {
        this.message.error(this.i18n.fanyi('common.copy-failed'));
      }
    } catch {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private getIntegrationId(app: TenantIntegrationAppViewModel): string {
    return this.getInstanceId(app);
  }

  private toTenantInstallation(source: any): TenantIntegrationInstallation | null {
    if (!source || typeof source !== 'object') {
      return null;
    }

    const instanceId = String(source.instanceId ?? source.integrationId ?? '');
    return {
      id: String(source.id ?? ''),
      instanceId,
      integrationId: instanceId,
      appId: String(source.appId ?? ''),
      tenantId: String(source.tenantId ?? ''),
      tenantType: source.tenantType,
      externalTenantId: source.externalTenantId,
      instanceSecret: source.instanceSecret ?? source.appSecret,
      appSecret: source.instanceSecret ?? source.appSecret,
      webhookUrl: source.webhookUrl,
      subscribedEvents: Array.isArray(source.subscribedEvents) ? source.subscribedEvents : [],
      installAckMode: source.installAckMode,
      installRequestedAt: source.installRequestedAt,
      installCompletedAt: source.installCompletedAt,
      lastCallbackAt: source.lastCallbackAt,
      operatorId: source.operatorId,
      status: source.status,
      createTime: source.createTime,
      updateTime: source.updateTime
    };
  }

  private upsertInstallation(
    installations: TenantIntegrationInstallation[],
    installation: TenantIntegrationInstallation
  ): TenantIntegrationInstallation[] {
    const next = installations.filter(
      item =>
        String(item.instanceId || item.integrationId || item.id) !==
        String(installation.instanceId || installation.integrationId || installation.id)
    );
    next.push(installation);
    return next;
  }

  private mergeApps(
    systemApps: IntegrationAppDefinition[],
    installations: TenantIntegrationInstallation[]
  ): TenantIntegrationAppViewModel[] {
    const installationMap = installations.reduce<Map<string, TenantIntegrationInstallation>>((acc, item) => {
      const key = this.normalizeAppId(item.appId);
      const existing = acc.get(key);
      if (!existing || this.compareInstallationPriority(item, existing) > 0) {
        acc.set(key, item);
      }
      return acc;
    }, new Map<string, TenantIntegrationInstallation>());

    return systemApps
      .map(app => {
        const installation = installationMap.get(this.normalizeAppId(app.appId)) ?? null;
        const installed = this.isInstalledInstallation(installation);
        return {
          ...app,
          controlPlaneHeaders: { ...app.controlPlaneHeaders },
          controlPlaneEndpoints: { ...app.controlPlaneEndpoints },
          supportedEvents: [...app.supportedEvents],
          displayControlPlaneBaseUrl: app.displayControlPlaneBaseUrl,
          displayInstallUrl: app.displayInstallUrl,
          installed,
          installUrl: this.buildInstallUrl(app.controlPlaneBaseUrl, app.controlPlaneEndpoints),
          installation
        };
      })
      .filter(app => app.installed || app.status === 'Active');
  }

  private compareInstallationPriority(current: TenantIntegrationInstallation, previous: TenantIntegrationInstallation): number {
    const currentInstalled = this.isInstalledInstallation(current);
    const previousInstalled = this.isInstalledInstallation(previous);
    if (currentInstalled !== previousInstalled) {
      return currentInstalled ? 1 : -1;
    }

    return this.getInstallationSortTime(current) - this.getInstallationSortTime(previous);
  }

  private getInstallationSortTime(installation: TenantIntegrationInstallation): number {
    return Number(
      installation.updateTime ?? installation.createTime ?? installation.installCompletedAt ?? installation.installRequestedAt ?? 0
    );
  }

  private isInstalledInstallation(installation: TenantIntegrationInstallation | null): boolean {
    if (!installation?.status) {
      return false;
    }

    return this.installedStatuses.has(installation.status);
  }

  private toIntegrationAppDefinition(source: any): IntegrationAppDefinition {
    const endpointMap = this.getEndpointMap(source);
    const normalizedBaseUrl = this.getBaseUrl(source.controlPlaneBaseUrl, endpointMap);

    return {
      id: source.id || source.appId,
      appId: source.appId,
      appName: source.appName,
      provider: source.provider,
      supportedEvents: Array.isArray(source.supportedEvents) ? source.supportedEvents : [],
      controlPlaneBaseUrl: normalizedBaseUrl,
      displayControlPlaneBaseUrl: String(source.controlPlaneBaseUrl ?? '').trim(),
      displayInstallUrl: String(source.installUrl ?? '').trim(),
      controlPlaneAuthType: source.controlPlaneAuthType ?? source.authType ?? 'None',
      controlPlaneSecret: source.controlPlaneSecret ?? source.secret,
      controlPlaneHeaders: { ...(source.controlPlaneHeaders ?? source.headers ?? {}) },
      controlPlaneEndpoints: this.normalizeEndpointValues(endpointMap, normalizedBaseUrl),
      status: source.status ?? 'Active'
    };
  }

  private getEndpointMap(source: any): Record<string, string> {
    if (source.controlPlaneEndpoints && Object.keys(source.controlPlaneEndpoints).length > 0) {
      return { ...source.controlPlaneEndpoints };
    }

    const entries: Array<[string, string | undefined]> = [
      ['install', source.installUrl],
      ['update', source.updateUrl],
      ['rotateSecret', source.rotateSecretUrl],
      ['uninstall', source.uninstallUrl]
    ];

    return entries.reduce<Record<string, string>>((acc, [key, value]) => {
      const normalizedValue = String(value ?? '').trim();
      if (normalizedValue) {
        acc[key] = normalizedValue;
      }
      return acc;
    }, {});
  }

  private getBaseUrl(controlPlaneBaseUrl: string | undefined, endpoints: Record<string, string>): string {
    if (controlPlaneBaseUrl) {
      return controlPlaneBaseUrl;
    }

    const urls = Object.values(endpoints).filter(value => /^https?:\/\//i.test(value));
    if (!urls.length) {
      return '';
    }

    try {
      const parsedUrls = urls.map(value => new URL(value));
      const origin = parsedUrls[0].origin;
      if (parsedUrls.some(item => item.origin !== origin)) {
        return origin;
      }

      const segmentLists = parsedUrls.map(item => item.pathname.split('/').filter(Boolean).slice(0, -1));
      const commonSegments =
        segmentLists[0]?.filter((_, index) => segmentLists.every(list => list[index] === segmentLists[0][index])) ?? [];
      const basePath = commonSegments.length ? `/${commonSegments.join('/')}` : '';
      return `${origin}${basePath}`;
    } catch {
      return urls[0];
    }
  }

  private normalizeEndpointValues(endpoints: Record<string, string>, baseUrl: string): Record<string, string> {
    if (!baseUrl) {
      return { ...endpoints };
    }

    return Object.entries(endpoints).reduce<Record<string, string>>((acc, [key, value]) => {
      const normalizedValue = value.trim();
      if (!/^https?:\/\//i.test(normalizedValue)) {
        acc[key] = normalizedValue;
        return acc;
      }

      try {
        const parsedBaseUrl = new URL(baseUrl);
        const parsedValue = new URL(normalizedValue);
        if (parsedBaseUrl.origin === parsedValue.origin && parsedValue.href.startsWith(parsedBaseUrl.href.replace(/\/$/, ''))) {
          const nextValue = parsedValue.href.slice(parsedBaseUrl.href.replace(/\/$/, '').length) || '/';
          acc[key] = nextValue.startsWith('/') ? nextValue : `/${nextValue}`;
          return acc;
        }
      } catch {}

      acc[key] = normalizedValue;
      return acc;
    }, {});
  }

  private buildInstallUrl(baseUrl: string, endpoints: Record<string, string>): string | null {
    const installEndpoint = String(endpoints['install'] ?? '').trim();
    const normalizedBaseUrl = baseUrl.trim();

    if (!installEndpoint && !normalizedBaseUrl) {
      return null;
    }

    if (/^https?:\/\//i.test(installEndpoint)) {
      return installEndpoint;
    }

    if (!normalizedBaseUrl) {
      return null;
    }

    if (!installEndpoint) {
      return normalizedBaseUrl;
    }

    if (/^https?:\/\//i.test(installEndpoint)) {
      return installEndpoint;
    }

    return `${normalizedBaseUrl.replace(/\/+$/, '')}/${installEndpoint.replace(/^\/+/, '')}`;
  }

  private normalizeAppId(appId: string): string {
    return String(appId ?? '')
      .trim()
      .toUpperCase();
  }

  private setSubscribedEvents(values: SupportedEventPattern[]): void {
    this.updateForm.controls.subscribedEvents.setValue(this.normalizeSupportedEvents(values));
    this.updateForm.controls.subscribedEvents.markAsDirty();
    this.updateForm.controls.subscribedEvents.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  private normalizeSupportedEvents(values: Array<string | SupportedEventPattern> | undefined): SupportedEventPattern[] {
    if (!Array.isArray(values)) {
      return [];
    }

    const normalized = values
      .map(value => this.normalizeSupportedEvent(value))
      .filter((value): value is SupportedEventPattern => Boolean(value));

    return Array.from(new Set(normalized));
  }

  private normalizeSupportedEvent(value: string | SupportedEventPattern): SupportedEventPattern | null {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
      return null;
    }

    if (rawValue.includes('*')) {
      return rawValue.toLowerCase();
    }

    const snakeCaseValue = rawValue
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[-\s]+/g, '_')
      .toLowerCase();

    if (snakeCaseValue.includes('.')) {
      const [rawDomain, rawEvent] = snakeCaseValue.split('.', 2);
      const normalizedDomain = rawDomain.replace(/_/g, '');
      const domainPattern = SUPPORTED_EVENT_DOMAIN_TO_PATTERN[normalizedDomain];
      if (domainPattern) {
        const domainPrefix = domainPattern.replace(/\.\*$/, '');
        return rawEvent ? `${domainPrefix}.${rawEvent}` : domainPattern;
      }
      return snakeCaseValue;
    }

    const normalizedDomainKey = snakeCaseValue.replace(/_/g, '');
    if (SUPPORTED_EVENT_DOMAIN_TO_PATTERN[normalizedDomainKey]) {
      return SUPPORTED_EVENT_DOMAIN_TO_PATTERN[normalizedDomainKey];
    }

    return snakeCaseValue;
  }
}
